import { ComponentNameToModifyAttribute } from '@source-components-react/types';

/**
 * Mapping of attribute names to their corresponding editor components.
 *
 * The RuleCMS editor dynamically loads the appropriate editor component for each attribute
 * that users can edit. This mapping ensures that the correct component is loaded when
 * editing a given attribute.
 *
 * The attributes defined here are shared across all source components available for user modification.
 */
export const componentNameToModifyAttribute: ComponentNameToModifyAttribute = {
  'icon-name-3-resolutions': 'icon-name-3-resolutions',
  'content-text-3-resolutions': 'content-text-3-resolutions',
  // r-button owns its own label and icon attributes — one component per
  // documented setting — but reuses the editors those names already resolve
  // to, so no new editor component is needed for either.
  'button-label-3-resolutions': 'content-text-3-resolutions',
  'button-icon-3-resolutions': 'icon-name-3-resolutions',
  // The preset is an object of three axes rather than one scalar, and the
  // button takes a single link rather than the numbered list rich text uses,
  // so neither fits an existing editor. Position and disabled are plain
  // two-value choices and go through the shared select.
  'button-preset-3-resolutions': 'button-preset-3-resolutions',
  'button-link-3-resolutions': 'button-link-3-resolutions',
  'button-icon-position-3-resolutions': 'button-icon-position-3-resolutions',
  'button-disabled-3-resolutions': 'button-disabled-3-resolutions',
  // Both of the divider's settings are closed sets of a handful of values, so
  // both go through the shared select and neither needs an editor built for it.
  'divider-style-3-resolutions': 'divider-style-3-resolutions',
  'divider-thickness-3-resolutions': 'divider-thickness-3-resolutions',
  // The embed URL is the one field in the library whose value is accepted or
  // refused rather than merely stored, so it gets an editor that can say which
  // it was while the author is still looking at the field. Its title is plain
  // text, and the other two are closed sets.
  'embed-url-3-resolutions': 'embed-url-3-resolutions',
  'embed-title-3-resolutions': 'image-text-3-resolutions',
  'embed-aspect-ratio-3-resolutions': 'embed-aspect-ratio-3-resolutions',
  'embed-loading-3-resolutions': 'embed-loading-3-resolutions',
  'enclosing-tag-3-resolutions': 'enclosing-tag-3-resolutions',
  'links-3-resolutions': 'links-3-resolutions',
  'inline-styles-3-resolutions': 'inline-styles-3-resolutions',
  // r-list owns its own attribute names — one component per documented
  // setting — but reuses existing editors wherever one fits: the multi-line
  // text field for its items, the numbered link list for its links, and the
  // plain text field for where numbering starts (the number editor is
  // pixel-specific, and a numbering start is not a length). Kind, marker and
  // reversed are closed sets and go through the shared select.
  'list-items-3-resolutions': 'content-text-3-resolutions',
  'list-kind-3-resolutions': 'list-kind-3-resolutions',
  'list-marker-3-resolutions': 'list-marker-3-resolutions',
  'list-start-3-resolutions': 'image-text-3-resolutions',
  'list-reversed-3-resolutions': 'list-reversed-3-resolutions',
  'list-links-3-resolutions': 'links-3-resolutions',
  'list-inline-styles-3-resolutions': 'inline-styles-3-resolutions',
  // r-accordion owns its own attribute names — one component per documented
  // setting — and reuses existing editors for everything that fits: the
  // multi-line text field for the summary and the detail, and the numbered
  // link list for the detail's links. Whether it starts open and whether the
  // summary is a heading are closed sets and go through the shared select.
  'accordion-summary-3-resolutions': 'content-text-3-resolutions',
  'accordion-detail-3-resolutions': 'content-text-3-resolutions',
  'accordion-open-3-resolutions': 'accordion-open-3-resolutions',
  'accordion-heading-level-3-resolutions': 'accordion-heading-level-3-resolutions',
  'accordion-links-3-resolutions': 'links-3-resolutions',
  'accordion-inline-styles-3-resolutions': 'inline-styles-3-resolutions',
  // r-mount: the name is a constrained identifier and reuses the shared
  // plain-text editor; the props list is a repeatable key/type/value row
  // and needs its own editor.
  'mount-name-3-resolutions': 'image-text-3-resolutions',
  'mount-props-3-resolutions': 'mount-props-3-resolutions',
  'content-title-3-resolutions': 'content-title-3-resolutions',
  'media-selector-3-resolutions': 'media-selector-3-resolutions',
  'pass-through-image-attributes-alt-3-resolutions': 'image-alt-text-3-resolutions',
  'pass-through-image-attributes-width-3-resolutions': 'image-intrinsic-width-3-resolutions',
  'pass-through-image-attributes-height-3-resolutions': 'image-intrinsic-height-3-resolutions',
  'pass-through-image-attributes-sizes-3-resolutions': 'image-sizes-3-resolutions',
  'pass-through-image-attributes-loading-3-resolutions': 'image-loading-3-resolutions',
  'pass-through-image-attributes-fetchpriority-3-resolutions': 'image-fetchpriority-3-resolutions',
  'cloudinary-transformations-3-resolutions': 'cloudinary-transformations-3-resolutions',
  'video-selector-3-resolutions': 'video-selector-3-resolutions',
  'video-poster-selector-3-resolutions': 'video-poster-selector-3-resolutions',
  'pass-through-video-attributes-controls-3-resolutions': 'video-controls-3-resolutions',
  'pass-through-video-attributes-autoplay-3-resolutions': 'video-autoplay-3-resolutions',
  'pass-through-video-attributes-loop-3-resolutions': 'video-loop-3-resolutions',
  'pass-through-video-attributes-muted-3-resolutions': 'video-muted-3-resolutions',
  'pass-through-video-attributes-playsinline-3-resolutions': 'video-playsinline-3-resolutions',
  'pass-through-video-attributes-preload-3-resolutions': 'video-preload-3-resolutions',
  'pass-through-video-attributes-width-3-resolutions': 'video-intrinsic-width-3-resolutions',
  'pass-through-video-attributes-height-3-resolutions': 'video-intrinsic-height-3-resolutions',
  'video-transformations-3-resolutions': 'video-transformations-3-resolutions',
};
